import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { FormErrorBanner } from '../shared/FormErrorBanner';
import type { User, FormData, UserFieldErrors } from '../../hooks/useUsersList';

interface UserFormModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  editingUser: User | null;
  currentUser: User | null;
  formData: FormData;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
  fieldErrors: UserFieldErrors;
  formErrors: string[];
  onSubmit: () => Promise<void>;
  submitting: boolean;
}

export const UserFormModal: React.FC<UserFormModalProps> = ({
  isOpen,
  onOpenChange,
  editingUser,
  currentUser,
  formData,
  setFormData,
  fieldErrors,
  formErrors,
  onSubmit,
  submitting,
}) => {
  const { t } = useTranslation();

  const renderFieldErrors = (errors: string[]) => {
    if (errors.length === 0) return null;

    return (
      <ul className="space-y-1 text-sm text-destructive">
        {errors.map((error, index) => (
          <li key={`${error}-${index}`}>{t(error)}</li>
        ))}
      </ul>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {editingUser ? t('admin.users.edit') : t('admin.users.add')}
          </DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <FormErrorBanner errors={formErrors} />

          <div className="space-y-2">
            <label className="text-sm font-medium">{t('admin.users.name')} *</label>
            <input
              className="flex h-10 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
            {renderFieldErrors(fieldErrors.name)}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">{t('admin.users.email')} *</label>
            <input
              type="email"
              className="flex h-10 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
            {renderFieldErrors(fieldErrors.email)}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">
              {t('admin.users.role')} *
            </label>
            <select
              className={`flex h-10 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400 ${
                editingUser?.id === currentUser?.id || currentUser?.role !== 'owner'
                  ? 'bg-slate-100 text-slate-500 cursor-not-allowed'
                  : 'bg-white'
              }`}
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              disabled={editingUser?.id === currentUser?.id || currentUser?.role !== 'owner'}
            >
              {/* Admin option only for owners */}
              {(currentUser?.role === 'owner' || editingUser?.role === 'admin') && (
                <option value="admin">{t('admin.users.roles.admin')}</option>
              )}
              <option value="manager">{t('admin.users.roles.manager')}</option>
            </select>
            {currentUser?.role !== 'owner' && (
              <p className="text-xs text-muted-foreground">
                {t('admin.users.ownerRequiredForAdmin')}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">
              {t('admin.users.password')}
              {editingUser && (
                <span className="font-normal text-muted-foreground ml-1">
                  {t('admin.users.password_hint')}
                </span>
              )}
            </label>
            <input
              type="password"
              className="flex h-10 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required={!editingUser}
            />
            {renderFieldErrors(fieldErrors.password)}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">
              {t('admin.users.passwordConfirmation')}
            </label>
            <input
              type="password"
              className="flex h-10 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
              value={formData.password_confirmation}
              onChange={(e) => setFormData({ ...formData, password_confirmation: e.target.value })}
              required={!editingUser || !!formData.password}
            />
            {renderFieldErrors(fieldErrors.password_confirmation)}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button onClick={onSubmit} disabled={submitting}>
            {submitting ? t('common.saving') : t('common.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
